/**
 * Tool Detail — full configuration page (Wave 3 PR-A — real API).
 *
 * Three tabs:
 *   Overview     — identity, description, parameters (read-only view of
 *                  the spec), agent grants summary
 *   Configuration — handler-config editor (HTTP / database / MCP /
 *                  OpenAPI variants), admin valves JSON editor,
 *                  user-valves schema JSON editor, policy + active
 *   Test         — POST /:id/test against the real handler config;
 *                  shows latency + ok/error
 *
 * Drops the prior Metrics tab (mock 24h chart) and Test Runner mock
 * latency. When real metrics ship, Metrics returns as a real tab.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, Settings2, Shield, Sliders, Play,
  AlertTriangle,
  Save, RefreshCw, Lock, X, Copy, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  RadioGroup, RadioGroupItem,
} from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useWorkspaceTool,
  useUpdateWorkspaceTool,
  useTestWorkspaceTool,
  useDeleteWorkspaceTool,
  useCreateWorkspaceTool,
  useToolAgentGrants,
  useGrantToolToAgents,
  useRevokeToolFromAgent,
  type ToolPolicy,
  type ToolSource,
} from '@/lib/use-workspace-tools';
import { useAgents } from '@/lib/use-agents';
import { cn } from '@/lib/utils';
// PR-C-MINIMAL — POLICY_DESCRIPTIONS + SOURCE_META + getSourceMeta()
// pulled into the shared tools-authoring module; helper-page used the
// shorter copy variant previously, but now matches the create-page copy
// so users see the same explanation on every surface.
import {
  POLICY_DESCRIPTIONS,
  getSourceMeta,
} from '@/components/tools-authoring-shared/tools-shared.constants';
import { parseJsonTyped } from '@/components/tools-authoring-shared/tools-shared.helpers';
// #258 verification — Test tab policy → button matrix lives in a
// pure helper so the contract is unit-testable without RTL.
import {
  isRunTestEnabledForPolicy,
  isForbiddenPolicy,
  requiresConfirmDialog,
  runTestButtonLabel,
  runTestButtonTooltip,
  toolPolicyTransitionKey,
} from './tools-detail.helpers';

export const Route = createFileRoute('/studio/tools/$toolId')({
  component: ToolDetailPage,
});

type DetailTab = 'overview' | 'config' | 'grants' | 'test';

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function getToolInputSchema(
  tool: WorkspaceToolDetailRow,
  isSystem: boolean,
): unknown {
  if (isSystem) return tool.inputSchema ?? {};
  if (isRecord(tool.spec) && 'input' in tool.spec) {
    return tool.spec.input ?? {};
  }
  return tool.spec ?? {};
}

function getToolOutputSchema(
  tool: WorkspaceToolDetailRow,
  isSystem: boolean,
): unknown {
  if (isSystem) return tool.outputSchema ?? {};
  if (isRecord(tool.spec) && 'output' in tool.spec) {
    return tool.spec.output ?? {};
  }
  return {};
}

function ToolDetailPage() {
  const { toolId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch, isFetching } = useWorkspaceTool(toolId);
  const updateTool = useUpdateWorkspaceTool();
  const testTool = useTestWorkspaceTool();
  // Round 4 fix #248 — tenant tool detail page now exposes Clone +
  // Delete affordances on the header alongside Save. Clone deep-copies
  // the row into a fresh tenant tool with `_copy` suffix; Delete is
  // gated by a confirm dialog and is a no-op for built-in rows
  // (server returns 409 with a clear message, surfaced as a toast).
  const deleteTool = useDeleteWorkspaceTool();
  const createTool = useCreateWorkspaceTool();

  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [cloning, setCloning] = useState(false);

  // Editable form state — mirrors the row, populated when data lands.
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [policy, setPolicy] = useState<ToolPolicy>('auto_approve');
  const [isActive, setIsActive] = useState(true);
  const [handlerConfigText, setHandlerConfigText] = useState('{}');
  const [adminValvesText, setAdminValvesText] = useState('{}');
  const [userValvesSchemaText, setUserValvesSchemaText] = useState('{}');

  // Test runner state
  const [testResult, setTestResult] = useState<{
    success: boolean;
    latencyMs: number;
    error: string | null;
    at: number;
  } | null>(null);

  // Pull row data into form when it arrives.
  const tool = data?.data;
  // Round 5 fix #258 — clear stale test result whenever the tool
  // identity OR its persisted policy changes. Without this, the QA
  // saw an `OK 145ms` from an earlier Auto-policy probe still
  // labelled green under a Require-Approval / Forbidden state, which
  // was misleading: the result no longer reflected the current
  // policy. Scoping by `(tool.id, tool.policy)` discards any prior
  // result the moment the user navigates to a new tool or the saved
  // policy on this tool changes (after a Save).
  //
  // #1093 — Pre-fix this effect ALSO fired immediately when the tool
  // GET resolved from undefined → defined on the initial mount, AND
  // (more subtly) any time TanStack Query refetched the tool detail
  // (window-focus, manual refetch, post-mutation invalidate) the
  // effect re-fired with the SAME tool.id + tool.policy because the
  // dependency-array primitives didn't change — that part was fine.
  // BUT during the gap between `testTool.mutate(...)` firing and its
  // `onSuccess` landing, if the GET refetched and React batched a
  // re-render that included the effect, the timing could clear
  // `testResult` immediately after `onResult({...})` had set it.
  // The reported symptom — "click → button stays enabled, no result
  // panel, no toast" — matches a write-then-clear race. We now
  // guard the clear with a ref so the effect only runs when the
  // tool's identity OR persisted policy ACTUALLY transitioned to a
  // new value the user can observe (post-Save / route-change), not
  // on every refetch tick. Initial mount is also a no-op because the
  // ref starts undefined and we only set it after the first observed
  // transition.
  const lastClearedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tool) return;
    // #258 — composite key extracted to a pure helper so the
    // transition logic is unit-testable; see tools-detail.helpers.ts.
    const key = toolPolicyTransitionKey(tool.id, tool.policy);
    if (key === null) return;
    if (lastClearedKeyRef.current === null) {
      // First observation — no stale result to clear. Just record the
      // baseline so subsequent transitions can be detected.
      lastClearedKeyRef.current = key;
      return;
    }
    if (lastClearedKeyRef.current !== key) {
      lastClearedKeyRef.current = key;
      setTestResult(null);
    }
  }, [tool?.id, tool?.policy]);
  useEffect(() => {
    if (!tool) return;
    // Issue #203 — defensive defaults on form hydration. The API GET is
    // now guarded against missing/partial fields, but the SPA must also
    // tolerate a stale-cache or in-flight response shape without
    // crashing during render. Every read goes through `??` so a NULL
    // / undefined column never becomes
    //   "Cannot read properties of null (reading 'replace')"
    // inside the policy badge or
    //   "Cannot read properties of undefined (reading 'length')"
    // inside the handler-config Textarea.
    setDisplayName(tool.displayName ?? '');
    setDescription(tool.description ?? '');
    setPolicy(((tool.policy as ToolPolicy) ?? 'auto_approve') as ToolPolicy);
    setIsActive(tool.isActive ?? true);
    setHandlerConfigText(JSON.stringify(tool.handlerConfig ?? {}, null, 2));
    setAdminValvesText(JSON.stringify(tool.adminValves ?? {}, null, 2));
    setUserValvesSchemaText(
      JSON.stringify(tool.userValvesSchema ?? {}, null, 2),
    );
  }, [tool]);

  const dirty = useMemo(() => {
    if (!tool) return false;
    if (displayName !== tool.displayName) return true;
    if (description !== tool.description) return true;
    if (policy !== tool.policy) return true;
    if (isActive !== tool.isActive) return true;
    if (handlerConfigText !== JSON.stringify(tool.handlerConfig ?? {}, null, 2))
      return true;
    if (adminValvesText !== JSON.stringify(tool.adminValves ?? {}, null, 2))
      return true;
    if (
      userValvesSchemaText !==
      JSON.stringify(tool.userValvesSchema ?? {}, null, 2)
    )
      return true;
    return false;
  }, [
    tool,
    displayName,
    description,
    policy,
    isActive,
    handlerConfigText,
    adminValvesText,
    userValvesSchemaText,
  ]);

  const handleSave = async () => {
    if (!tool) return;

    const handlerConfig = parseJsonTyped(handlerConfigText, 'Handler config');
    if (!handlerConfig.ok) {
      toast.error(handlerConfig.err);
      return;
    }
    const adminValves = parseJsonTyped(adminValvesText, 'Admin valves');
    if (!adminValves.ok) {
      toast.error(adminValves.err);
      return;
    }
    const userValvesSchema = parseJsonTyped(userValvesSchemaText, 'User valves schema');
    if (!userValvesSchema.ok) {
      toast.error(userValvesSchema.err);
      return;
    }

    try {
      await updateTool.mutateAsync({
        id: tool.id,
        displayName,
        description,
        policy,
        isActive,
        handlerConfig: handlerConfig.value,
        adminValves: adminValves.value,
        userValvesSchema: userValvesSchema.value,
      });
      toast.success('Saved');
    } catch (err) {
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const handleReset = () => {
    if (!tool) return;
    setDisplayName(tool.displayName);
    setDescription(tool.description);
    setPolicy(tool.policy as ToolPolicy);
    setIsActive(tool.isActive);
    setHandlerConfigText(JSON.stringify(tool.handlerConfig ?? {}, null, 2));
    setAdminValvesText(JSON.stringify(tool.adminValves ?? {}, null, 2));
    setUserValvesSchemaText(JSON.stringify(tool.userValvesSchema ?? {}, null, 2));
  };

  // #1093 — dead `handleTest` removed. The active probe path now lives
  // entirely inside `<TestTab>` (see `runTest` below); the outer
  // `handleTest` was unused and contained the same pre-fix
  // `setTestResult(null)` synchronous-clear pattern that the QA reports
  // attributed to the disappearing result panel. Keeping a dead copy
  // of the bad pattern in the file made future refactors risky.

  // Round 4 fix #248 — Clone + Delete actions on tenant tool detail.
  const handleDelete = async () => {
    if (!tool) return;
    try {
      await deleteTool.mutateAsync({ id: tool.id });
      toast.success(`Deleted "${tool.displayName}"`);
      navigate({ to: '/studio/tools' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Delete failed', { description: msg });
    } finally {
      setConfirmDelete(false);
    }
  };

  const handleClone = async () => {
    if (!tool) return;
    setCloning(true);
    try {
      // Pick a unique-ish slug; the user can rename in the detail view
      // afterward. We don't probe the catalog for collisions because
      // names are tenant-scoped and free-form (the unique constraint
      // is on (org, name) — POST will surface a 409 if needed).
      const cloneSuffix = '_copy';
      const cloneName = `${tool.name}${cloneSuffix}`;
      const cloneDisplay = `${tool.displayName} (copy)`;
      // Parse handlerConfig if it's a string fallback; the API stores
      // it as JSON so it should already be an object.
      const handlerConfig =
        tool.handlerConfig && typeof tool.handlerConfig === 'object'
          ? tool.handlerConfig
          : {};
      const created = await createTool.mutateAsync({
        name: cloneName,
        displayName: cloneDisplay,
        description: tool.description,
        // The catalog supports mcp / openapi / custom / database;
        // tenant tools never have toolSource='builtin' or 'system' so
        // it's safe to pass through unchanged. Cast once for typing.
        toolSource: tool.toolSource as 'mcp' | 'openapi' | 'custom' | 'database',
        spec: {
          input: getToolInputSchema(tool, false),
          output: getToolOutputSchema(tool, false),
        },
        handlerConfig,
        namespace: tool.namespace,
        policy: tool.policy as ToolPolicy,
      });
      toast.success(`Cloned to "${cloneDisplay}"`, {
        duration: 2000,
        position: 'bottom-right',
      });
      navigate({ to: '/studio/tools/$toolId', params: { toolId: created.id } });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Clone failed', { description: msg });
    } finally {
      setCloning(false);
    }
  };

  // ─── Loading / error / not-found states ─────────────────────────────────

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-muted-foreground animate-pulse">Loading tool…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {error.message}</div>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!tool) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Settings2 className="h-12 w-12 text-muted-foreground/30 mb-3" />
        <h2 className="text-lg font-semibold">Tool not found</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          No tool with id <code className="font-mono text-xs">{toolId}</code>
        </p>
        <Button variant="outline" onClick={() => navigate({ to: '/studio/tools' })}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Tools
        </Button>
      </div>
    );
  }

  const sm = getSourceMeta(tool.toolSource);
  const Icon = sm.icon;
  // Studio Phase 3E: tools come from two surfaces — tenant
  // (workspace_tools) and system (system_tools). System tools are
  // read-only on the tenant side; the editor + delete + valves are
  // suppressed and a "Use in agent" affordance is surfaced instead.
  const isSystem = (tool as { scope?: string }).scope === 'system';
  const detailInputSchema = getToolInputSchema(tool, isSystem);
  const detailOutputSchema = getToolOutputSchema(tool, isSystem);

  // ─── Header ─────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-border/40 bg-background px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate({ to: '/studio/tools' })}
            aria-label="Back"
            title="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <div className={cn('h-8 w-8 rounded-md border flex items-center justify-center shrink-0', sm.cls)}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold leading-tight flex items-center gap-2">
              {tool.displayName}
              {isSystem && (
                <Badge
                  variant="outline"
                  className="text-[10px] bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30"
                >
                  System
                </Badge>
              )}
              {tool.isBuiltin && (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>Built-in tool</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </h1>
            <div className="font-mono text-xs text-muted-foreground">
              {/* Round 4 fix #247 — system tool slugs already include
                  the `system.` prefix (e.g. `system.calculate`). The
                  API also returns `namespace = 'system'` for catalog
                  filtering. Concatenating them produced
                  `system.system.calculate`. For system rows render the
                  slug verbatim; tenant rows keep the `<namespace>.<name>`
                  composition since their `name` is the unqualified slug. */}
              {isSystem ? tool.name : `${tool.namespace}.${tool.name}`} · v{tool.version}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Refresh tool detail"
            aria-label="Refresh tool detail"
            className="h-8"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          </Button>
          {isSystem ? (
            // System tools: read-only on the tenant side. Surface the
            // funnel into agents instead of an edit affordance.
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => navigate({ to: '/agents/builder', search: { tool: tool.id } as never })}
              title="Use this tool in a new agent"
            >
              Use in agent
            </Button>
          ) : (
            <>
              {/* Round 4 fix #248 — tenant tool detail exposes Clone +
                  Delete alongside Save. Built-in rows (is_builtin=TRUE,
                  seeded by the platform) skip Delete since the server
                  returns 409 — they can be deactivated from the
                  Configuration tab instead. */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleClone}
                disabled={cloning || createTool.isPending}
                className="gap-1.5"
                title="Duplicate this tool into a new draft"
              >
                <Copy className="h-3.5 w-3.5" />
                {cloning ? 'Cloning…' : 'Clone'}
              </Button>
              {!tool.isBuiltin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmDelete(true)}
                  disabled={deleteTool.isPending}
                  className="gap-1.5 text-destructive hover:bg-destructive/10"
                  title="Delete this tool"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </Button>
              )}
              {dirty && (
                <Button variant="outline" size="sm" onClick={handleReset} disabled={updateTool.isPending}>
                  <X className="h-3.5 w-3.5 mr-1" /> Discard
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!dirty || updateTool.isPending}
                className="gap-1.5"
              >
                <Save className="h-3.5 w-3.5" />
                {updateTool.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </>
          )}
        </div>
      </div>

      {isSystem && (
        <div className="border-b border-violet-500/30 bg-violet-500/5 px-6 py-2 text-xs text-violet-700 dark:text-violet-300">
          <strong>System tool</strong> — managed by the platform. Tenants see the contract (input/output schema, policy, side effects, version) but not the implementation. Grant this tool to your agents to use it.
        </div>
      )}

      {/* Tab strip */}
      <div className="border-b border-border/40 bg-background px-6">
        {/* fix-wave-4 #990 — role="tablist" + aria-label so the tab
            group is identified as such by assistive tech. */}
        <nav role="tablist" aria-label="Tool detail sections" className="flex items-center gap-0.5">
          <TabBtn
            label="Overview"
            icon={Settings2}
            active={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
          />
          <TabBtn
            label="Configuration"
            icon={Sliders}
            active={activeTab === 'config'}
            onClick={() => setActiveTab('config')}
          />
          <TabBtn
            label="Agents"
            icon={Shield}
            active={activeTab === 'grants'}
            onClick={() => setActiveTab('grants')}
          />
          <TabBtn
            label="Test"
            icon={Play}
            active={activeTab === 'test'}
            onClick={() => setActiveTab('test')}
          />
        </nav>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto p-6 max-w-4xl space-y-5">
        {activeTab === 'overview' && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Identity</CardTitle>
                <CardDescription>
                  How this tool appears to agents and users in your workspace.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="t-displayName" className="text-xs">Display name</Label>
                  <Input
                    id="t-displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    disabled={tool.isBuiltin || isSystem}
                    className="text-sm h-8"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="t-description" className="text-xs">Description</Label>
                  <Textarea
                    id="t-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={tool.isBuiltin || isSystem}
                    rows={3}
                    className="text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-muted-foreground">Source</div>
                    <Badge variant="outline" className={cn('text-[10px] gap-1 mt-1', sm.cls)}>
                      <Icon className="h-2.5 w-2.5" />
                      {sm.label}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Identifier</div>
                    <code className="font-mono text-[11px]">
                      {isSystem ? tool.name : `${tool.namespace}.${tool.name}`}
                    </code>
                  </div>
                </div>
              </CardContent>
            </Card>

            {isSystem ? (
              <>
                {/* System tools surface input + output schemas explicitly
                    (sysadmin-private source_config is never returned). */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Input schema</CardTitle>
                    <CardDescription>
                      Inputs the agent must provide when calling this tool.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <pre className="rounded border bg-muted/30 p-3 text-xs font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
                      {JSON.stringify(detailInputSchema, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Output schema</CardTitle>
                    <CardDescription>
                      Shape of the response the tool returns to the agent.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <pre className="rounded border bg-muted/30 p-3 text-xs font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
                      {JSON.stringify(detailOutputSchema, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
                {tool.sideEffects && tool.sideEffects.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Side effects</CardTitle>
                      <CardDescription>
                        Risk markers declared by the publisher. The platform's policy reflects this classification.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-1.5 flex-wrap">
                        {tool.sideEffects.map((s) => (
                          <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Input schema</CardTitle>
                    <CardDescription>
                      JSON schema declared at registration time. Agents see
                      these parameters when planning a tool call.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <pre className="rounded border bg-muted/30 p-3 text-xs font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
                      {JSON.stringify(detailInputSchema, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Output schema</CardTitle>
                    <CardDescription>
                      Response shape preserved with the tenant tool manifest.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <pre className="rounded border bg-muted/30 p-3 text-xs font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
                      {JSON.stringify(detailOutputSchema, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}

        {activeTab === 'config' && isSystem && (
          <Card>
            <CardContent className="py-6 text-sm space-y-2">
              <p>
                <strong>Configuration is sysadmin-managed</strong> for system tools.
                Tenants don't edit policy, valves, or handler config — these belong to
                the platform-published row.
              </p>
              <p className="text-xs text-muted-foreground">
                Active state: <Badge variant="outline" className="text-[10px] ml-1">{tool.isActive ? 'Active' : 'Inactive'}</Badge>{' '}
                · Approval policy: <Badge variant="outline" className="text-[10px] ml-1">{tool.policy.replace('_', ' ')}</Badge>
              </p>
              <p className="text-xs text-muted-foreground">
                If you need a customized version, file a request — Q3 forbids cloning system tools (their executor wiring is platform-private).
              </p>
            </CardContent>
          </Card>
        )}

        {activeTab === 'config' && !isSystem && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between rounded border p-3">
                  <div>
                    <div className="text-sm">Active</div>
                    <div className="text-[11px] text-muted-foreground">
                      Inactive tools cannot be invoked even if an agent has them granted.
                    </div>
                  </div>
                  <Switch checked={isActive} onCheckedChange={setIsActive} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  Approval policy
                </CardTitle>
                <CardDescription>
                  Controls whether tool invocations require human confirmation.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup value={policy} onValueChange={(v) => setPolicy(v as ToolPolicy)}>
                  {(['auto_approve', 'require_approval', 'forbidden'] as ToolPolicy[]).map((p) => (
                    <label
                      key={p}
                      className="flex items-start gap-3 rounded border p-3 cursor-pointer hover:bg-accent"
                    >
                      <RadioGroupItem value={p} id={`policy-${p}`} className="mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm font-medium capitalize">{p.replace('_', ' ')}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {POLICY_DESCRIPTIONS[p]}
                        </div>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Handler configuration</CardTitle>
                <CardDescription>
                  Source-specific connection details. Edits are validated as
                  JSON before save.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={handlerConfigText}
                  onChange={(e) => setHandlerConfigText(e.target.value)}
                  rows={10}
                  className="text-xs font-mono"
                  spellCheck={false}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Admin valves</CardTitle>
                <CardDescription>
                  Workspace-wide configuration values (API keys, base URLs)
                  applied to every invocation. Tenant-private — never sent
                  to the LLM in tool descriptions.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={adminValvesText}
                  onChange={(e) => setAdminValvesText(e.target.value)}
                  rows={6}
                  className="text-xs font-mono"
                  spellCheck={false}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">User valves schema</CardTitle>
                <CardDescription>
                  JSON schema describing per-user overrides users can set in
                  their personal preferences. Each user gets their own row in
                  <code> workspace_tool_user_valves</code>.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={userValvesSchemaText}
                  onChange={(e) => setUserValvesSchemaText(e.target.value)}
                  rows={6}
                  className="text-xs font-mono"
                  spellCheck={false}
                />
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'grants' && (
          <GrantsSection toolId={tool.id} isSystem={isSystem} />
        )}

        {activeTab === 'test' && (
          <TestTab
            tool={tool}
            isSystem={isSystem}
            testTool={testTool}
            onResult={setTestResult}
            testResult={testResult}
          />
        )}
      </div>

      {/* Round 4 fix #248 — delete-confirm dialog. */}
      <AlertDialog
        open={confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this tool?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete <strong>{tool.displayName}</strong>{' '}
              (<code className="font-mono text-xs">
                {isSystem ? tool.name : `${tool.namespace}.${tool.name}`}
              </code>)?
              Any agents that reference this tool will lose access immediately.
              Existing audit-log entries are kept; the tool itself is removed
              from the catalog.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteTool.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={deleteTool.isPending}
            >
              {deleteTool.isPending ? 'Deleting…' : 'Delete tool'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TabBtn({
  label,
  icon: Icon,
  active,
  onClick,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  active: boolean;
  onClick: () => void;
}) {
  // fix-wave-4 #990 (STUDIO-TOOL-DETAIL-001) — Pre-fix this <button>
  // had no `role="tab"` / `aria-selected`, so screen-reader users and
  // axe-core `aria-required-attr` checks could not identify the strip
  // as a tab pattern. The cycle-3.5 matrix probe (getByRole('tab', {
  // name: 'Overview' })) also returned 0 nodes. We add the canonical
  // WAI-ARIA tab attributes — `role="tab"` + `aria-selected` — so the
  // strip is correctly identified as a tab pattern even though we
  // don't (yet) host the tabpanel siblings under a `role="tablist"`.
  // A future a11y sweep can fold this into the shared `<Tabs />`
  // primitive without changing this contract.
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors',
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

// ─── Phase 5E — Test tab (#10 follow-through, Q9) ─────────────────────────
//
// Schema-aware test runner. The user can type a JSON input matching
// the tool's spec / inputSchema, run a connectivity probe, and see
// the result. Per Q9 the test is a probe today (real invocation is
// the agent runtime's job); for write-policy tools we surface the
// confirm dialog before sending. Real invocation runner is tracked
// for v1.2 — see audit doc.

interface TestTabProps {
  tool: WorkspaceToolDetailRow;
  isSystem: boolean;
  testTool: ReturnType<typeof useTestWorkspaceTool>;
  testResult: { success: boolean; latencyMs: number; error: string | null; at: number } | null;
  onResult: (r: { success: boolean; latencyMs: number; error: string | null; at: number } | null) => void;
}

interface WorkspaceToolDetailRow {
  id: string;
  name: string;
  isActive: boolean;
  policy: string;
  toolSource: string;
  spec?: unknown;
  inputSchema?: unknown;
  outputSchema?: unknown;
}

function TestTab({ tool, isSystem, testTool, testResult, onResult }: TestTabProps) {
  const [inputText, setInputText] = useState('{}');
  const [confirmOpen, setConfirmOpen] = useState(false);
  // #258 — policy → button-matrix lives in tools-detail.helpers so
  // the contract is pinned by unit tests. Cast to the helper's
  // narrowed ToolPolicy union; the route's broader `tool.policy:
  // string` is normalised at hydrate-time but TS still wants the
  // assertion here.
  const toolPolicy = tool.policy as
    | 'auto_approve'
    | 'require_approval'
    | 'forbidden';
  const requiresApproval = requiresConfirmDialog(toolPolicy);
  const isForbidden = isForbiddenPolicy(toolPolicy);

  const schema = getToolInputSchema(tool, isSystem);

  const runTest = () => {
    let parsedInput: unknown = undefined;
    if (inputText.trim()) {
      try {
        parsedInput = JSON.parse(inputText);
      } catch (e) {
        toast.error('Invalid JSON', {
          description: e instanceof Error ? e.message : 'Could not parse test input.',
        });
        return;
      }
    }
    // #1093 — Pre-fix `onResult(null)` ran BEFORE `testTool.mutate(...)`.
    // The mutation hook's `isPending` flag flips true synchronously
    // inside `.mutate(...)`, so the button-disabled state landed on the
    // next render. But several QA reports saw "button stays enabled +
    // no toast + no result panel" after a click — meaning the parent's
    // testResult-clear useEffect (keyed on tool?.id + tool?.policy) was
    // racing the result back to null on the same render that the
    // mutation finished. Symptom: the panel never shows.
    //
    // Defense-in-depth:
    //   - Clear the OLD result inside the mutation's onSuccess (after
    //     a successful run) rather than synchronously before mutate.
    //     A stale OK card stays visible while the next probe is in
    //     flight, which is honest UX (the spinner on the button shows
    //     pending; the old result is labelled with its own timestamp).
    //   - The success/error paths both ALWAYS set a result so the user
    //     always gets explicit feedback even if the toast layer is
    //     mounted late.
    testTool.mutate(
      { id: tool.id, input: parsedInput },
      {
        onSuccess: (result) => {
          onResult({ ...result, at: Date.now() });
          if (result.success) {
            toast.success(`Test OK (${result.latencyMs}ms)`);
          } else {
            toast.error(`Test failed (${result.latencyMs}ms)`, {
              description: result.error ?? 'Unknown error',
            });
          }
        },
        onError: (err) => {
          onResult({ success: false, latencyMs: 0, error: err.message, at: Date.now() });
          toast.error('Test failed', { description: err.message });
        },
      },
    );
  };

  const handleClick = () => {
    if (isForbidden) {
      toast.error('Tool is forbidden', { description: 'Forbidden-policy tools cannot be invoked.' });
      return;
    }
    if (requiresApproval) {
      setConfirmOpen(true);
      return;
    }
    runTest();
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Play className="h-4 w-4 text-muted-foreground" />
            Test connection
          </CardTitle>
          <CardDescription>
            HTTP / OpenAPI: probes the URL using the configured method (write methods downgrade
            to HEAD to avoid mutating the target). <code>{`{{variable}}`}</code> placeholders are
            substituted with values from your test input below; remaining placeholders use
            <code> __test__</code> so the URL parses. MCP: probes <code>/health</code>. Database +
            built-in + Custom Function: have no external probe and return an explanatory message.
            Real invocation runs at agent-call time — see audit doc for the v1.2 deferred runner.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Test input (JSON)</Label>
            <Textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              rows={6}
              className="text-xs font-mono"
              placeholder='{ "equipmentTag": "P-401" }'
              spellCheck={false}
            />
            <p className="text-[10px] text-muted-foreground">
              Match the input schema on the Overview tab.
            </p>
          </div>
          <div className="rounded border bg-muted/30 p-2 max-h-48 overflow-y-auto">
            <p className="text-[10px] text-muted-foreground mb-1">Schema reference:</p>
            <pre className="text-[10px] font-mono whitespace-pre-wrap">
              {JSON.stringify(schema, null, 2)}
            </pre>
          </div>
          {/* Round 5 fix #258 — Run Test enable matrix:
                - auto_approve     → enabled, no confirm dialog
                - require_approval → enabled, click opens AlertDialog
                                     so the user explicitly confirms
                                     before the probe runs.
                - forbidden        → disabled.
              The prior code already enabled require_approval, but the
              QA reported the button looked unusable; clarify by
              prefixing the require_approval label with "Confirm &"
              so the action is unambiguous, and surface a one-line
              note explaining what will happen on click. */}
          <div className="flex items-center gap-2">
            <Button
              onClick={handleClick}
              // #258 — `isRunTestEnabledForPolicy` returns true for
              // auto_approve + require_approval (regression-pin for
              // the original bug, which disabled require_approval).
              // The runtime gates (`isPending`, `!isActive`) stay
              // separate.
              disabled={
                testTool.isPending ||
                !tool.isActive ||
                !isRunTestEnabledForPolicy(toolPolicy)
              }
              className="gap-1.5"
              title={runTestButtonTooltip(toolPolicy)}
            >
              <Play className="h-3.5 w-3.5" />
              {runTestButtonLabel(toolPolicy, testTool.isPending)}
            </Button>
            {!tool.isActive && (
              <div className="text-[11px] text-muted-foreground">
                Activate the tool to run a test.
              </div>
            )}
            {requiresApproval && tool.isActive && (
              <div className="text-[11px] text-muted-foreground">
                Approval gate — confirmation required before probe runs.
              </div>
            )}
            {isForbidden && (
              <div className="text-[11px] text-destructive">
                Forbidden — testing disabled.
              </div>
            )}
          </div>
          {/* #1093 — Live result region. ARIA live="polite" so screen
                readers announce the result without grabbing focus. The
                `data-testid` lets the QA matrix locate this panel by
                stable id regardless of styling changes; the inner
                `data-result-status` (ok|failed|pending) lets the same
                probes assert state without parsing classnames.
                The pending branch was added in this fix so the user
                ALWAYS sees explicit feedback after clicking Run test —
                even before the network response lands. Pre-fix the
                only feedback was the button's "Testing…" label, which
                multiple QA reports said was easy to miss; combined
                with any swallowed onSuccess (see runTest comment),
                the user could click Run test and observe absolutely
                nothing change. */}
          <div
            aria-live="polite"
            aria-atomic="true"
            data-testid="tool-test-result"
            data-result-status={
              testTool.isPending
                ? 'pending'
                : testResult
                  ? testResult.success
                    ? 'ok'
                    : 'failed'
                  : 'idle'
            }
          >
            {testTool.isPending && !testResult && (
              <Card className="border border-muted-foreground/30">
                <CardContent className="py-3 text-sm flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-muted-foreground/10 text-muted-foreground border-muted-foreground/30"
                  >
                    RUNNING
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    Probing tool…
                  </span>
                </CardContent>
              </Card>
            )}
            {testResult && (
              <Card
                // Force a fresh render on every new result so React
                // can never re-use a stale Card subtree from a prior
                // probe (defense-in-depth against the QA-reported
                // "no panel after click" symptom).
                key={testResult.at}
                className={cn(
                  'border',
                  testResult.success
                    ? 'border-emerald-500/40'
                    : 'border-destructive/40',
                )}
              >
                <CardContent className="py-3 text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-[10px]',
                        testResult.success
                          ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
                          : 'bg-destructive/10 text-destructive border-destructive/30',
                      )}
                    >
                      {testResult.success ? 'OK' : 'FAILED'}
                    </Badge>
                    <span className="font-mono text-xs">
                      {testResult.latencyMs}ms
                    </span>
                    <span className="text-[11px] text-muted-foreground ml-auto">
                      {new Date(testResult.at).toLocaleTimeString()}
                    </span>
                  </div>
                  {testResult.error && (
                    <div className="text-xs text-destructive">
                      {testResult.error}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Q9 — write-policy tools require explicit confirmation. */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm test invocation</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{tool.name}</strong> has a <code>require_approval</code> policy — it
              performs side effects (writes / network calls / modifications). The probe still
              downgrades write methods to HEAD to avoid mutation, but you should confirm before
              running. Proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmOpen(false); runTest(); }}>
              Run test
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Phase 5A — Agent Grants section (#149) ────────────────────────────────

function GrantsSection({ toolId, isSystem }: { toolId: string; isSystem: boolean }) {
  const { data, isLoading } = useToolAgentGrants(toolId);
  const { data: agentsResp } = useAgents();
  const grant = useGrantToolToAgents(toolId);
  const revoke = useRevokeToolFromAgent(toolId);
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState('');

  const grantedIds = new Set((data?.items ?? []).map((g) => g.agentId));
  // useAgents returns the agent array directly (selected from { data, total }).
  const allAgents = agentsResp ?? [];
  const ungrantedAgents = allAgents.filter((a) => !grantedIds.has(a.id));

  const toggle = (id: string) => {
    setSelectedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleGrant = () => {
    if (selectedAgents.size === 0) return;
    grant.mutate(
      { agentIds: Array.from(selectedAgents), notes: notes.trim() || undefined },
      {
        onSuccess: ({ inserted, skipped }) => {
          toast.success(`Granted to ${inserted} agent${inserted === 1 ? '' : 's'}${skipped ? ` (${skipped} already had access or invalid)` : ''}`);
          setSelectedAgents(new Set());
          setNotes('');
        },
        onError: (err) => toast.error('Grant failed', { description: err.message }),
      },
    );
  };

  const handleRevoke = (agentId: string) => {
    revoke.mutate({ agentId }, {
      onSuccess: ({ deleted }) => toast.success(deleted > 0 ? 'Revoked' : 'Already revoked'),
      onError: (err) => toast.error('Revoke failed', { description: err.message }),
    });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" />
            Agents using this tool
          </CardTitle>
          <CardDescription>
            {isSystem
              ? 'System tools are grantable to tenant agents (Q3). The list below shows which of your agents have been granted this system tool.'
              : "Agents in this tenant that can call this tool. Grants are bidirectional with the agent's tools list — revoking here removes the tool from the agent."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-sm text-muted-foreground py-4 text-center animate-pulse">
              Loading grants…
            </div>
          ) : (data?.items ?? []).length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center italic">
              No agents have been granted this tool yet.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {(data?.items ?? []).map((g) => (
                <li
                  key={g.grantId}
                  className="flex items-center justify-between rounded border p-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{g.agentName}</div>
                    {g.notes && (
                      <div className="text-[11px] text-muted-foreground truncate">{g.notes}</div>
                    )}
                    <div className="text-[10px] text-muted-foreground tabular-nums">
                      Granted {new Date(g.grantedAt).toLocaleString()}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-[11px] text-destructive hover:bg-destructive/10"
                    onClick={() => handleRevoke(g.agentId)}
                    disabled={revoke.isPending}
                  >
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Grant to agents</CardTitle>
          <CardDescription>
            Pick one or more of your agents to grant this tool to. The agent
            will be able to call it in their next run.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {ungrantedAgents.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">
              All your agents already have this tool granted.
            </div>
          ) : (
            <div className="border rounded p-2 max-h-48 overflow-y-auto space-y-1">
              {ungrantedAgents.map((a) => (
                <label
                  key={a.id}
                  className="flex items-center gap-2 text-sm hover:bg-accent/30 px-1 py-0.5 cursor-pointer rounded"
                >
                  <input
                    type="checkbox"
                    checked={selectedAgents.has(a.id)}
                    onChange={() => toggle(a.id)}
                    className="h-3.5 w-3.5"
                  />
                  <span className="font-medium">{a.name}</span>
                  {a.category && (
                    <Badge variant="outline" className="text-[9px]">{a.category}</Badge>
                  )}
                </label>
              ))}
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Notes (optional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why are you granting this tool?"
              className="text-sm h-8"
            />
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleGrant}
              disabled={selectedAgents.size === 0 || grant.isPending}
            >
              {grant.isPending ? 'Granting…' : `Grant to ${selectedAgents.size} agent${selectedAgents.size === 1 ? '' : 's'}`}
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
